// Integrytas Group — Shell chrome bridge.
// The main title bar (TabManagerShell header) sits OUTSIDE each page's
// PersonalizationProvider, yet must drive two per-page concerns from one place:
//   * Inspect (field-lineage) mode — GLOBAL, so it persists as you navigate.
//   * Personalization — the ACTIVE page publishes its context here so the header
//     can render a single personalization control bound to whatever page is shown.
// Providers read this to (a) source the global inspect flag and (b) publish
// themselves when active. Absent (e.g. a page rendered standalone in tests),
// providers fall back to local state — nothing here is required.

import { createContext, useContext } from 'react';
import type { PersonalizationCtx } from './use_personalization';

export interface ShellChrome {
  /** Global inspect mode — one switch for the whole workspace. */
  isInspecting:     boolean;
  setInspecting:    (v: boolean) => void;
  toggleInspecting: () => void;
  /** internal_code of the currently focused tab. */
  activeCode:       string;
  /** The active page's personalization context, re-rendered into the header. */
  activePersonalization: PersonalizationCtx | null;
  /** A page's provider publishes (or clears) itself here when it is the active tab. */
  publishPersonalization: (pageCode: string, ctx: PersonalizationCtx | null) => void;
}

export const ShellChromeContext = createContext<ShellChrome | null>(null);

/** Null when rendered outside the shell (standalone page / tests). */
export function useShellChrome(): ShellChrome | null {
  return useContext(ShellChromeContext);
}
